// Off-screen 9:16 (1080x1920) PNG share card renderer for the post-session
// widget. NOT a UI surface the user sees on screen — mounted at
// position:absolute, left:-10000 by the host so layout happens but the user
// can't see it. The card image is captured via ViewShot and handed to the
// platform's share mechanism (native share sheet on mobile, Web Share API /
// download on web-rnw).
//
// Lifted from apps/mobile/src/logger/ShareCardRenderer.tsx (2026-08-18) — the
// only mobile-local piece was the theme source (useTheme()) and the BodyMap/
// isDark wiring; react-native-view-shot ships a real web backend (html2canvas,
// verified via its own src/RNViewShot.web.ts + lib/RNViewShot.web.js — Vite's
// `.web.js`-first resolve.extensions already picks it), so <ViewShot> itself
// needs no platform split. Theme-aware (2026-07-01, Antenore: "il share card
// dovrebbe rispettare il tema"): colours come from the caller's resolved theme
// (palette + heatmap ramp via data.bodyMapValue) instead of a pinned token set.
//
// Mobile keeps a 5-line shim (apps/mobile/src/logger/ShareCardRenderer.tsx)
// that injects useTheme(); web-rnw calls this directly with resolveTheme().

import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { SvgXml } from 'react-native-svg';
import ViewShot, { type ViewShotRef } from 'react-native-view-shot';

import { JL_LOGO_SVG, type Palette, type ShareCardData } from '@jackedlogs/shared';

import { BodyMap } from '../bodymap/BodyMap';
import { Icon } from '../Icon/Icon';
import { ScaleLegend } from '../primitives/ScaleLegend';
import type { UiTheme } from '../theme';

const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1920;

export type ShareCardRendererProps = {
  theme: UiTheme;
  /** UiTheme doesn't itself carry the scheme name — the bodymap figure border
   *  needs the flag alongside (same split BodyMap's own props use). */
  isDark?: boolean;
  data: ShareCardData;
  figure: 'masculine' | 'feminine';
};

// forwardRef so the parent can call ref.current.capture() to produce the PNG.
// ViewShot exposes a typed capture method on the ref instance, on every
// platform it supports (native + web).
export const ShareCardRenderer = forwardRef<ViewShotRef, ShareCardRendererProps>(
  function ShareCardRenderer({ theme, isDark = false, data, figure }, ref) {
    const { t } = useTranslation();
    const { palette } = theme;
    const styles = makeStyles(palette);
    const deltaColour =
      data.delta === null
        ? palette.ink3
        : data.delta.direction === 'up'
          ? palette.complete
          : data.delta.direction === 'down'
            ? palette.danger
            : palette.ink3;

    return (
      <ViewShot
        ref={ref}
        options={{
          format: 'png',
          quality: 1.0,
          width: CARD_WIDTH,
          height: CARD_HEIGHT,
        }}
        style={styles.card}
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.brand}>JackedLogs</Text>
          <Text style={styles.date}>{data.date}</Text>
        </View>

        {/* Session label */}
        <Text style={styles.sessionLabel}>{data.sessionLabel}</Text>

        {/* Dominant number — numberOfLines=1 so the unit suffix never
            falls off the value at large values (defensive; the web
            twin had this break with Inter Tight weight 900). */}
        <View style={styles.dominantBlock}>
          <Text style={styles.dominantValue} numberOfLines={1} adjustsFontSizeToFit>
            {data.dominantValue}
          </Text>
          <View style={styles.dominantSub}>
            <Text style={styles.dominantLabel} numberOfLines={1} adjustsFontSizeToFit>
              {data.dominantLabel}
            </Text>
            {data.dominantIsPr ? (
              <View style={styles.prPill}>
                <Text style={styles.prPillText}>{t('mobile.share.newPR')}</Text>
                {data.delta !== null ? (
                  <Text style={styles.prPillDelta}>{data.delta.value}</Text>
                ) : null}
              </View>
            ) : data.delta !== null ? (
              <Text style={[styles.delta, { color: deltaColour }]}>
                {data.delta.value}
              </Text>
            ) : null}
          </View>
        </View>

        {/* Top lifts — each row carries the exercise name, the topSet
            (kg x reps) that produced the e1RM, and the e1RM itself.
            PR rows show a trophy next to the e1RM. */}
        {data.topLifts.length > 0 ? (
          <View style={styles.liftsBlock}>
            <Text style={styles.sectionTitle}>{t('mobile.share.topLifts')}</Text>
            {data.topLifts.map((lift) => (
              <View key={lift.slug} style={styles.liftRow}>
                <View style={styles.liftMain}>
                  <Text style={styles.liftName} numberOfLines={1}>
                    {lift.name}
                  </Text>
                  <Text style={styles.liftSet}>{lift.topSetText}</Text>
                </View>
                {lift.e1rmText !== null || lift.isPr ? (
                  <View style={styles.liftE1rmRow}>
                    {lift.isPr ? (
                      <Icon name="trophy" size={22} color={palette.pr} />
                    ) : null}
                    <Text style={styles.liftE1rm}>{lift.e1rmText ?? ''}</Text>
                  </View>
                ) : null}
              </View>
            ))}
          </View>
        ) : null}

        {/* Muscle bars */}
        {data.muscleBars.length > 0 ? (
          <View style={styles.barsBlock}>
            {data.muscleBars.map((bar) => (
              <View key={bar.slug} style={styles.barRow}>
                <View style={styles.barLabelRow}>
                  <Text style={styles.barLabel}>{bar.label}</Text>
                  <Text style={styles.barValue}>{bar.volumeText}</Text>
                </View>
                <View style={styles.barTrack}>
                  <View
                    style={[
                      styles.barFill,
                      { width: `${Math.max(2, bar.fillPct * 100)}%` },
                    ]}
                  />
                </View>
              </View>
            ))}
          </View>
        ) : null}

        {/* Bodymap dual */}
        {Object.keys(data.bodyMapValue).length > 0 ? (
          <View style={styles.bodyBlock}>
            <BodyMap
              mode="display"
              view="front"
              value={data.bodyMapValue}
              theme={theme}
              isDark={isDark}
              scale={2.4}
              figure={figure}
              layout="dual"
            />
            <ScaleLegend
              variant="ramp"
              theme={theme}
              stops={theme.heatmapRamp}
              scale={2}
              style={styles.shareLegend}
            />
          </View>
        ) : null}

        {/* Footer — stat-strip: one value-over-label cell per stat
            (Volume · Duration · Density · [TUT] · [Impulse] · Calories ·
            Effort), wrapped and centred. */}
        <View style={styles.footer}>
          <View style={styles.statStrip}>
            {data.footerStats.map((stat) => (
              <View key={stat.label} style={styles.statCell}>
                <Text style={styles.statValue} numberOfLines={1} adjustsFontSizeToFit>
                  {stat.value}
                </Text>
                <Text style={styles.statLabel} numberOfLines={1}>
                  {stat.label}
                </Text>
              </View>
            ))}
          </View>
          {data.socialHandle ? (
            <Text style={styles.handle}>{data.socialHandle}</Text>
          ) : null}
        </View>

        {/* JL brand signature, bottom-right. Stamps the captured PNG so
            the brand mark survives even when an IG / Stories avatar
            covers the header logo on the recipient's feed. */}
        <View style={styles.signature} pointerEvents="none">
          <SvgXml xml={JL_LOGO_SVG} width={96} height={96} />
        </View>
      </ViewShot>
    );
  },
);

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    card: {
      width: CARD_WIDTH,
      height: CARD_HEIGHT,
      backgroundColor: p.bg,
      paddingHorizontal: 64,
      paddingTop: 80,
      paddingBottom: 80,
      justifyContent: 'space-between',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    brand: {
      color: p.brand500,
      fontSize: 32,
      fontWeight: '800',
      letterSpacing: 1,
    },
    date: {
      color: p.ink3,
      fontSize: 24,
      fontWeight: '600',
    },
    sessionLabel: {
      color: p.ink3,
      fontSize: 36,
      fontWeight: '700',
      letterSpacing: 4,
      textTransform: 'uppercase',
      textAlign: 'center',
      marginTop: 60,
    },
    dominantBlock: {
      alignItems: 'center',
      marginTop: 12,
    },
    dominantValue: {
      color: p.brand500,
      fontSize: 160,
      fontWeight: '900',
      fontVariant: ['tabular-nums'],
      lineHeight: 180,
    },
    dominantSub: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
      marginTop: 8,
    },
    dominantLabel: {
      color: p.ink,
      fontSize: 32,
      fontWeight: '600',
    },
    delta: {
      fontSize: 28,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    prPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 14,
      paddingVertical: 6,
      backgroundColor: p.brand500,
      borderRadius: 999,
    },
    prPillText: {
      color: p.inkOnBrand,
      fontSize: 22,
      fontWeight: '800',
      letterSpacing: 1.5,
      textTransform: 'uppercase',
    },
    prPillDelta: {
      color: p.inkOnBrand,
      fontSize: 22,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    signature: {
      position: 'absolute',
      right: 48,
      bottom: 48,
      opacity: 0.92,
    },
    liftsBlock: {
      gap: 12,
      marginTop: 24,
    },
    sectionTitle: {
      color: p.ink3,
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: 3,
      textTransform: 'uppercase',
      marginBottom: 4,
    },
    liftRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 16,
    },
    liftMain: {
      flex: 1,
      minWidth: 0,
    },
    liftName: {
      color: p.ink,
      fontSize: 26,
      fontWeight: 700,
    },
    liftSet: {
      color: p.ink3,
      fontSize: 20,
      fontWeight: 500,
      fontVariant: ['tabular-nums'],
      marginTop: 2,
    },
    liftE1rmRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    liftE1rm: {
      color: p.brand500,
      fontSize: 28,
      fontWeight: 800,
      fontVariant: ['tabular-nums'],
    },
    barsBlock: {
      gap: 16,
      marginTop: 24,
    },
    barRow: {
      gap: 6,
    },
    barLabelRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'baseline',
    },
    barLabel: {
      color: p.ink,
      fontSize: 24,
      fontWeight: '700',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    barValue: {
      color: p.ink3,
      fontSize: 22,
      fontWeight: '600',
      fontVariant: ['tabular-nums'],
    },
    barTrack: {
      height: 12,
      backgroundColor: p.surface2,
      borderRadius: 6,
      overflow: 'hidden',
    },
    barFill: {
      height: '100%',
      backgroundColor: p.brand500,
      borderRadius: 6,
    },
    bodyBlock: {
      alignItems: 'center',
      marginTop: 24,
      flexShrink: 1,
    },
    // The ramp key below the figure: stretched to the card width (insets so
    // it doesn't run edge-to-edge) so its flexed stops have a bounded width.
    shareLegend: {
      alignSelf: 'stretch',
      paddingHorizontal: 120,
      marginTop: 20,
    },
    footer: {
      alignItems: 'center',
      gap: 20,
      marginTop: 16,
    },
    statStrip: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'flex-start',
      columnGap: 48,
      rowGap: 20,
    },
    statCell: {
      alignItems: 'center',
      minWidth: 120,
    },
    statValue: {
      color: p.ink,
      fontSize: 40,
      fontWeight: '800',
      fontVariant: ['tabular-nums'],
    },
    statLabel: {
      color: p.ink3,
      fontSize: 20,
      fontWeight: '700',
      letterSpacing: 2,
      textTransform: 'uppercase',
      marginTop: 4,
    },
    handle: {
      color: p.ink3,
      fontSize: 22,
      fontWeight: '500',
    },
  });
